<template>
  <div>
    <!-- Progress Indicator -->
    <v-overlay :value="isProcessingOrder" z-index="9999">
      <div class="order-progress-container">
        <div class="progress-card">
          <v-card>
            <v-card-title class="justify-center">
              <v-icon left :color="progressColor">{{ progressIcon }}</v-icon>
              {{ progressTitle }}
            </v-card-title>
            
            <v-card-text class="text-center">
              <v-progress-linear
                :value="progressPercentage"
                :color="progressColor"
                height="8"
                striped
                class="mb-3"
              ></v-progress-linear>
              
              <div class="progress-message">
                {{ progressMessage }}
              </div>
              
              <!-- Step indicators -->
              <div class="steps-container mt-4">
                <div
                  v-for="(step, index) in orderSteps"
                  :key="index"
                  :class="[
                    'step-indicator',
                    {
                      'active': index === currentStepIndex,
                      'completed': index < currentStepIndex,
                      'pending': index > currentStepIndex
                    }
                  ]"
                >
                  <div class="step-icon">
                    <v-icon small :color="getStepColor(index)">
                      {{ getStepIcon(index) }}
                    </v-icon>
                  </div>
                  <div class="step-label">{{ step.label }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-overlay>

    <!-- Enhanced Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="error-header">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Erreur lors de la commande
        </v-card-title>
        
        <v-card-text>
          <div class="error-content">
            <div class="error-message mb-3">
              {{ errorMessage }}
            </div>
            
            <v-alert type="info" dense outlined class="mb-3">
              <small>
                <v-icon small left>mdi-shield-check</v-icon>
                Vos fonds n'ont pas été débités. Vous pouvez réessayer en toute sécurité.
              </small>
            </v-alert>

            <div v-if="retryCount > 0" class="retry-info">
              <small class="grey--text">
                Tentative {{ retryCount }} / {{ maxRetries }}
              </small>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelOrder">
            Annuler
          </v-btn>
          <v-btn color="primary" @click="retryOrder" :loading="isRetrying">
            <v-icon left>mdi-refresh</v-icon>
            Réessayer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'OrderEnhancer',

  props: {
    isProcessingOrder: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentStepIndex: 0,
      progressPercentage: 0,
      showErrorDialog: false,
      errorMessage: '',
      isRetrying: false,
      retryCount: 0,
      maxRetries: 3,
      lastOrderData: null,

      orderSteps: [
        { label: 'Validation', icon: 'mdi-check-circle', color: 'blue' },
        { label: 'Traitement', icon: 'mdi-cog', color: 'orange' },
        { label: 'Sauvegarde', icon: 'mdi-content-save', color: 'green' },
        { label: 'Terminé', icon: 'mdi-check', color: 'success' }
      ]
    }
  },

  computed: {
    progressTitle() {
      if (this.currentStepIndex < this.orderSteps.length) {
        return this.orderSteps[this.currentStepIndex].label
      }
      return 'Traitement terminé'
    },

    progressMessage() {
      const messages = [
        'Vérification des données de commande...',
        'Traitement de votre commande...',
        'Enregistrement de la commande...',
        'Commande créée avec succès!'
      ]
      return messages[this.currentStepIndex] || 'En cours...'
    },

    progressColor() {
      if (this.currentStepIndex < this.orderSteps.length) {
        return this.orderSteps[this.currentStepIndex].color
      }
      return 'success'
    },

    progressIcon() {
      if (this.currentStepIndex < this.orderSteps.length) {
        return this.orderSteps[this.currentStepIndex].icon
      }
      return 'mdi-check'
    }
  },

  methods: {
    // Enhanced order creation with progress tracking
    async enhanceOrderCreation(originalOrderFunction, orderData) {
      this.lastOrderData = orderData
      this.currentStepIndex = 0
      this.progressPercentage = 0
      this.retryCount = 0
      
      return this.attemptOrderCreation(originalOrderFunction, orderData)
    },

    async attemptOrderCreation(originalOrderFunction, orderData, isRetry = false) {
      if (isRetry) {
        this.retryCount++
      }

      try {
        // Step 1: Validation
        this.updateProgress(0, 25)
        await this.delay(500) // Visual feedback
        
        // Step 2: Processing
        this.updateProgress(1, 50)
        await this.delay(300)
        
        // Step 3: Saving
        this.updateProgress(2, 75)
        
        // Call the original order function
        const result = await originalOrderFunction(orderData)
        
        // Step 4: Completed
        this.updateProgress(3, 100)
        await this.delay(500)
        
        this.$emit('orderSuccess', { orderData, result })
        return { success: true, data: result }
        
      } catch (error) {
        console.error('Order creation failed:', error)
        
        // Check if we should retry
        if (this.shouldRetry(error) && this.retryCount < this.maxRetries) {
          this.showError(this.getErrorMessage(error), true)
        } else {
          this.showError(this.getErrorMessage(error), false)
        }
        
        return { success: false, error: error.message }
      }
    },

    updateProgress(stepIndex, percentage) {
      this.currentStepIndex = stepIndex
      this.progressPercentage = percentage
    },

    showError(message, canRetry) {
      this.errorMessage = message
      this.showErrorDialog = true
      
      if (!canRetry) {
        this.$emit('orderFailed', { error: message, retryCount: this.retryCount })
      }
    },

    async retryOrder() {
      this.isRetrying = true
      this.showErrorDialog = false
      
      try {
        await this.delay(500)
        await this.attemptOrderCreation(
          this.lastOriginalFunction, 
          this.lastOrderData, 
          true
        )
      } finally {
        this.isRetrying = false
      }
    },

    cancelOrder() {
      this.showErrorDialog = false
      this.$emit('orderCancelled', { retryCount: this.retryCount })
    },

    shouldRetry(error) {
      // Define which errors should trigger a retry
      const retryableErrors = [
        'Network Error',
        'timeout',
        'ECONNABORTED',
        'Request failed',
        500, 502, 503, 504 // Server errors
      ]
      
      return retryableErrors.some(retryable => 
        error.message?.includes(retryable) || 
        error.code === retryable ||
        error.response?.status === retryable
      )
    },

    getErrorMessage(error) {
      // Map specific errors to user-friendly messages
      const errorResponse = error.response
      
      // Handle backend specific errors
      if (errorResponse) {
        const status = errorResponse.status
        const data = errorResponse.data
        
        // Handle 424 error - Order already exists
        if (status === 424) {
          return data.message || 'Une commande existe déjà pour cette date et cet horaire.'
        }
        
        // Handle 400 errors - Bad request
        if (status === 400) {
          return data.message || 'Les données de la commande sont invalides.'
        }
        
        // Handle 401 - Unauthorized
        if (status === 401) {
          return 'Votre session a expiré. Veuillez vous reconnecter.'
        }
        
        // Handle 403 - Forbidden
        if (status === 403) {
          return 'Vous n\'avez pas les droits pour effectuer cette action.'
        }
        
        // Handle 500 errors - Server errors
        if (status >= 500) {
          return 'Erreur du serveur. Veuillez réessayer dans quelques instants.'
        }
        
        // Return backend message if available
        if (data && data.message) {
          return data.message
        }
      }
      
      // Network errors
      if (error.message?.includes('Network Error')) {
        return 'Problème de connexion. Vérifiez votre connexion internet.'
      }
      
      if (error.message?.includes('timeout')) {
        return 'La requête a expiré. Le serveur met du temps à répondre.'
      }
      
      return error.message || 'Une erreur inattendue s\'est produite.'
    },

    getStepColor(index) {
      if (index < this.currentStepIndex) return 'success'
      if (index === this.currentStepIndex) return this.progressColor
      return 'grey'
    },

    getStepIcon(index) {
      if (index < this.currentStepIndex) return 'mdi-check'
      return this.orderSteps[index].icon
    },

    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },

    // Store original function for retries
    setOriginalFunction(fn) {
      this.lastOriginalFunction = fn
    }
  }
}
</script>

<style scoped>
.order-progress-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
}

.progress-card {
  min-width: 400px;
  max-width: 500px;
}

.progress-message {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
}

.steps-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}

.step-indicator:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 15px;
  right: -50%;
  width: 100%;
  height: 2px;
  background: #e0e0e0;
  z-index: -1;
}

.step-indicator.completed:not(:last-child)::after {
  background: #4CAF50;
}

.step-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  border: 2px solid #e0e0e0;
  transition: all 0.3s ease;
}

.step-indicator.active .step-icon {
  background: #e3f2fd;
  border-color: #2196F3;
}

.step-indicator.completed .step-icon {
  background: #e8f5e8;
  border-color: #4CAF50;
}

.step-label {
  font-size: 12px;
  text-align: center;
  color: #666;
  font-weight: 500;
}

.step-indicator.active .step-label {
  color: #2196F3;
  font-weight: 600;
}

.step-indicator.completed .step-label {
  color: #4CAF50;
}

.error-header {
  background: linear-gradient(135deg, #ffebee 0%, #fce4ec 100%);
  border-bottom: 1px solid #e0e0e0;
}

.error-content {
  padding: 8px 0;
}

.error-message {
  font-size: 16px;
  line-height: 1.5;
  color: #333;
}

.retry-info {
  text-align: right;
  margin-top: 8px;
}

/* Mobile responsive */
@media (max-width: 600px) {
  .progress-card {
    min-width: 90vw;
    margin: 0 16px;
  }
  
  .steps-container {
    flex-direction: column;
    gap: 16px;
  }
  
  .step-indicator:not(:last-child)::after {
    display: none;
  }
}
</style>